import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { db } from '@/lib/db';
import { getSupabase } from '@/lib/db/client';
import { validateDisplayName } from '@/lib/display-name';
import { callPublicEdgeFunction } from '@/lib/edge';
import {
  clearAllInviteState,
  persistInviteToken,
  persistOnboardingProgress,
  restoreOnboardingProgress,
} from '@/lib/invite-token';
import {
  buildAcceptInvitationPayload,
  createInitialOnboardingForm,
  isOnboardingCompleteFlagSet,
  isOnboardingFormComplete,
  markOnboardingComplete,
  mergeSavedOnboardingForm,
  ONBOARDING_APPROVED_STATES,
  ONBOARDING_TERMS_STEP,
  ONBOARDING_VERIFY_STEP,
  resolveSubmitOnboardingForm,
  type OnboardingFormState,
} from '@/lib/onboarding';
import { Check } from 'lucide-react';
import { EmailOtpVerify } from '@/components/auth/EmailOtpVerify';
import { Crest } from '@/components/shared/Crest';
import { Avatar } from '@/components/shared/Avatar';
import { TermsOfServicePanel } from '@/components/onboarding/TermsOfServicePanel';
import { InstallAppTip } from '@/components/shared/InstallAppTip';

interface Props {
  token: string;
  onComplete: () => void;
}

const AVATARS = [
  'golf:1', 'golf:2', 'golf:3', 'golf:4',
  'cartoon:1', 'cartoon:2', 'cartoon:3', 'cartoon:4',
  'service:1', 'service:2',
];

const INITIAL_FORM = createInitialOnboardingForm(AVATARS[0]);

function isAuthCallbackUrl(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.hash.includes('access_token=')
    || window.location.search.includes('code=');
}

async function waitForSession(maxWaitMs = 4000) {
  const deadline = Date.now() + maxWaitMs;
  while (Date.now() < deadline) {
    const { data: { session } } = await getSupabase().auth.getSession();
    if (session) return session;
    await new Promise((r) => window.setTimeout(r, 200));
  }
  return null;
}

export function OnboardingFlow({ token, onComplete }: Props) {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState<string | null>(null);
  const [form, setForm] = useState<OnboardingFormState>(INITIAL_FORM);
  const formRef = useRef(form);
  const submittingRef = useRef(false);

  formRef.current = form;

  const acceptInvitation = useCallback(async (formData: OnboardingFormState) => {
    const displayErr = validateDisplayName(formData.name, formData.handle);
    if (displayErr) throw new Error(displayErr);
    if (!isOnboardingFormComplete(formData)) {
      throw new Error('Please complete all onboarding steps before joining.');
    }

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const { data: { session } } = await getSupabase().auth.getSession();
    if (!session) throw new Error('Verify your email first — enter the 6-digit code from your email.');

    const body = buildAcceptInvitationPayload(formData, token, session.user.id);
    const response = await fetch(`${supabaseUrl}/functions/v1/accept-invitation`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      throw new Error(payload?.error || 'Onboarding failed');
    }

    markOnboardingComplete();
    clearAllInviteState(token);
    await db.auth.refresh();
    onComplete();
  }, [token, onComplete]);

  const tryAutoFinish = useCallback(async (savedForm?: Partial<OnboardingFormState>) => {
    if (isOnboardingCompleteFlagSet()) return;
    const resolved = resolveSubmitOnboardingForm(
      formRef.current,
      savedForm,
      INITIAL_FORM,
    );
    if (submittingRef.current || !isOnboardingFormComplete(resolved)) return;
    const session = await waitForSession(isAuthCallbackUrl() ? 8000 : 2000);
    if (!session) return;

    submittingRef.current = true;
    setSubmitting(true);
    setError(null);
    try {
      await acceptInvitation(resolved);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }, [acceptInvitation]);

  useEffect(() => {
    persistInviteToken(token);

    const saved = restoreOnboardingProgress(token);
    if (saved?.form) {
      setForm((prev) => mergeSavedOnboardingForm(saved.form as Partial<OnboardingFormState>, prev));
    }

    void (async () => {
      try {
        const inv = await callPublicEdgeFunction<{
          status: string;
          email: string | null;
        }>('validate-invitation', { token });
        if (inv.email) {
          setInviteEmail(inv.email);
          setForm((prev) => (prev.email ? prev : { ...prev, email: inv.email!.trim() }));
        }
      } catch {
        // accept-invitation validates on submit
      }

      const savedForm = saved?.form as Partial<OnboardingFormState> | undefined;

      if (saved?.step === ONBOARDING_VERIFY_STEP) {
        setStep(ONBOARDING_VERIFY_STEP);
        void tryAutoFinish(savedForm);
        return;
      }

      if (saved?.step != null && saved.step > 0) {
        setStep(saved.step);
      }

      if (isAuthCallbackUrl() || saved?.step === ONBOARDING_VERIFY_STEP) {
        void tryAutoFinish(savedForm);
      }
    })();
  }, [token, tryAutoFinish]);

  useEffect(() => {
    if (step !== ONBOARDING_VERIFY_STEP) return;

    const finishIfReady = () => {
      void tryAutoFinish();
    };

    finishIfReady();
    const interval = window.setInterval(finishIfReady, 1500);

    const onStorage = (e: StorageEvent) => {
      if (e.key?.includes('auth-token') || e.key === 'bagtag_onboarding_complete') {
        finishIfReady();
      }
    };
    window.addEventListener('storage', onStorage);

    const { data: { subscription } } = getSupabase().auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') finishIfReady();
    });

    return () => {
      window.clearInterval(interval);
      window.removeEventListener('storage', onStorage);
      subscription.unsubscribe();
    };
  }, [step, tryAutoFinish]);

  const handleChange = (field: keyof OnboardingFormState, value: string | boolean) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      formRef.current = next;
      return next;
    });
  };

  const handleTosAccept = (accepted: boolean) => {
    setForm((prev) => {
      const next = {
        ...prev,
        tosAccepted: accepted,
        affiliationDisclaimerAcked: accepted,
      };
      formRef.current = next;
      return next;
    });
  };

  const nameError = useMemo(() => {
    if (step < 2) return null;
    if (!form.name.trim() || !form.handle.trim()) return null;
    return validateDisplayName(form.name, form.handle);
  }, [form.name, form.handle, step]);

  const canContinue = useMemo(() => {
    if (step === 0) return true;
    if (step === 1) return form.ageConfirmed && ONBOARDING_APPROVED_STATES.includes(form.state as typeof ONBOARDING_APPROVED_STATES[number]);
    if (step === 2) {
      const h = form.handle.trim();
      return form.name.trim().length >= 2 && h.length >= 3 && h.length <= 24 && !nameError;
    }
    if (step === 3) return form.email.trim().length > 0;
    if (step === 4) return form.handicap.trim().length > 0 && !nameError;
    if (step === ONBOARDING_TERMS_STEP) return form.tosAccepted && form.affiliationDisclaimerAcked && !nameError;
    return true;
  }, [form, step, nameError]);

  const persistCurrentForm = (nextStep: number) => {
    persistOnboardingProgress(nextStep, formRef.current, token);
  };

  const handleNext = () => {
    if (step === 2) persistCurrentForm(3);
    if (step === 3) persistCurrentForm(4);
    if (step === 4) persistCurrentForm(ONBOARDING_TERMS_STEP);
    setStep((s) => s + 1);
  };

  const handleEnterTheEagle = async () => {
    setSubmitting(true);
    setError(null);
    persistCurrentForm(ONBOARDING_TERMS_STEP);

    try {
      const live = formRef.current;
      const displayErr = validateDisplayName(live.name, live.handle);
      if (displayErr) {
        setError(displayErr);
        return;
      }
      if (!isOnboardingFormComplete(live)) {
        setError('Please complete all onboarding steps before joining.');
        return;
      }

      const session = await getSupabase().auth.getSession().then((r) => r.data.session);
      if (session) {
        await acceptInvitation(live);
        return;
      }

      await db.auth.signInForOnboarding(live.email, token);
      persistOnboardingProgress(ONBOARDING_VERIFY_STEP, formRef.current, token);
      setStep(ONBOARDING_VERIFY_STEP);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const stepContent: Record<number, ReactNode> = {
    0: (
      <div className="text-center">
        <div className="mx-auto mb-4 flex justify-center"><Crest size={72} /></div>
        <h2 className="font-serif text-2xl font-bold text-navy">Welcome to Bag Tag</h2>
        <p className="mt-2 text-sm leading-relaxed text-slate-600">Your club&apos;s social scoreboard. Post rounds, run games, climb the Bag Tag board, and celebrate aces together.</p>
        <div className="mt-6 grid grid-cols-2 gap-2 text-left text-xs text-slate-500">
          <div className="rounded-xl bg-slate-50 p-3">⛳ Post rounds & scores</div>
          <div className="rounded-xl bg-slate-50 p-3">🏆 Eagle Points board</div>
          <div className="rounded-xl bg-slate-50 p-3">🎯 Call Your Shot</div>
          <div className="rounded-xl bg-slate-50 p-3">🦅 Ace registry</div>
        </div>
        <InstallAppTip className="mt-6 text-left" dismissible={false} />
      </div>
    ),
    1: (
      <div className="space-y-4">
        <label className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 text-sm">
          <input type="checkbox" checked={form.ageConfirmed} onChange={(e) => handleChange('ageConfirmed', e.target.checked)} className="mt-1" />
          <span>I confirm I am 18 years of age or older.</span>
        </label>
        <div>
          <label className="mb-2 block text-sm font-semibold text-slate-700">State of residence</label>
          <select value={form.state} onChange={(e) => handleChange('state', e.target.value)} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm">
            <option value="">Select state</option>
            {ONBOARDING_APPROVED_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>
    ),
    2: (
      <div className="space-y-4">
        <Field label="Your full name" value={form.name} onChange={(v) => handleChange('name', v)} placeholder="First Last" hint="Shown on the scoreboard — first and last name" autoComplete="name" />
        <Field label="Username" value={form.handle} onChange={(v) => handleChange('handle', v)} placeholder="yourname" prefix="@" hint="Your @handle" autoComplete="off" />
        {nameError && <p className="text-sm text-red-600">{nameError}</p>}
      </div>
    ),
    3: (
      <div className="space-y-4">
        <Field
          label="Email"
          value={form.email}
          onChange={(v) => handleChange('email', v)}
          placeholder="you@example.com"
          type="email"
          readOnly={Boolean(inviteEmail)}
        />
        <Field label="Phone (optional)" value={form.phone} onChange={(v) => handleChange('phone', v)} placeholder="(555) 123-4567" type="tel" />
        <p className="text-xs text-slate-500">You&apos;ll verify this email as the last step before joining.</p>
      </div>
    ),
    4: (
      <div className="space-y-4">
        <Field label="Handicap index" value={form.handicap} onChange={(v) => handleChange('handicap', v)} placeholder="12.4" type="number" />
        <Field label="GHIN # (optional)" value={form.ghin} onChange={(v) => handleChange('ghin', v)} placeholder="1234567" />
        <Field label="Venmo (optional)" value={form.venmo} onChange={(v) => handleChange('venmo', v)} placeholder="@your-venmo" />
        <div>
          <label className="mb-2 block text-sm font-semibold text-slate-700">Avatar</label>
          <div className="grid grid-cols-5 gap-2">
            {AVATARS.map((avatar) => (
              <button key={avatar} type="button" onClick={() => handleChange('avatar', avatar)} className={`rounded-xl p-1 ${form.avatar === avatar ? 'ring-2 ring-gold' : ''}`}>
                <Avatar member={{ name: form.name || 'You', avatar }} size={40} />
              </button>
            ))}
          </div>
        </div>
      </div>
    ),
    5: (
      <TermsOfServicePanel accepted={form.tosAccepted} onAcceptChange={handleTosAccept} />
    ),
    6: (
      <EmailOtpVerify
        email={form.email.trim().toLowerCase()}
        headline="Almost there"
        subline={<>Enter the 6-digit code we sent to <strong>{form.email}</strong> to finish joining.</>}
        onVerify={async (code) => {
          await db.auth.verifyEmailOtp(form.email, code);
          await acceptInvitation(formRef.current);
        }}
        onResend={async () => {
          await db.auth.signInForOnboarding(form.email, token);
        }}
      />
    ),
  };

  const labels = ['Welcome', 'Eligibility', 'Profile', 'Contact', 'Golf info', 'Terms', 'Verify'];
  const totalSteps = 7;
  const isTermsStep = step === ONBOARDING_TERMS_STEP;

  return (
    <div className="min-h-screen bg-cream px-4 py-8 text-navy">
      <div className="mx-auto max-w-lg space-y-6">
        <div className="rounded-2xl bg-white p-6 shadow">
          {step > 0 && step < ONBOARDING_VERIFY_STEP && (
            <div className="mb-4 flex gap-1">
              {Array.from({ length: totalSteps - 2 }, (_, i) => i + 1).map((i) => (
                <div key={i} className={`h-1.5 flex-1 rounded-full ${step >= i ? 'bg-gold' : 'bg-slate-200'}`} />
              ))}
            </div>
          )}
          {step > 0 && <div className="mb-4 text-xs font-bold uppercase tracking-wider text-slate-400">{labels[step]}</div>}
          {stepContent[step]}
          {error && <div className="mt-4 rounded-xl bg-red-50 p-3 text-sm text-red-700">{error}</div>}
          {step !== ONBOARDING_VERIFY_STEP && (
            <div className="mt-6 flex gap-3">
              {step > 0 && (
                <button type="button" onClick={() => setStep((s) => s - 1)} disabled={submitting} className="flex-1 rounded-full border border-slate-200 py-3 text-sm font-semibold text-slate-600">Back</button>
              )}
              {!isTermsStep ? (
                <button type="button" onClick={handleNext} disabled={!canContinue || submitting} className="flex-1 rounded-full bg-navy py-3 text-sm font-bold text-cream disabled:opacity-50">
                  Continue
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => void handleEnterTheEagle()}
                  disabled={!canContinue || submitting}
                  className="flex flex-1 items-center justify-center gap-2 rounded-full bg-gold py-3 text-sm font-extrabold tracking-widest text-navy disabled:opacity-50"
                >
                  {submitting ? 'Sending code…' : (
                    <>
                      Enter the Eagle
                      <Check size={18} strokeWidth={3} />
                    </>
                  )}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = 'text', prefix, hint, readOnly, autoComplete }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; type?: string; prefix?: string; hint?: string; readOnly?: boolean; autoComplete?: string;
}) {
  return (
    <label className="block text-sm font-semibold text-slate-700">
      <span className="mb-2 block">{label}</span>
      <div className={`flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 ${readOnly ? 'bg-slate-50' : 'bg-white'}`}>
        {prefix && <span className="text-slate-400">{prefix}</span>}
        <input
          type={type}
          value={value}
          readOnly={readOnly}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="flex-1 bg-transparent text-sm outline-none"
          autoComplete={autoComplete ?? (type === 'email' ? 'email' : 'off')}
        />
      </div>
      {hint && <p className="mt-1 text-xs font-normal text-slate-500">{hint}</p>}
    </label>
  );
}
